import { Button, Icon } from "@sellar-io/components";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { DiscoveryHorizontalList } from "../components/DiscoveryHorizontalList";
import { ComponentProps, useState } from "react";
import { useDiscoveryDistributionSupplierList } from "../../../../hooks/api/v2/discovery/useDiscoveryDistributionSupplierList";
import { useMediaQuery } from "usehooks-ts";
import {
  DISCOVER_DISTRIBUTION,
  DISCOVER_SEARCH,
} from "../../../../constants/routes";
import { useNavigate } from "react-router";
import { DiscoveryV2DistributionSupplierCard } from "./DiscoveryV2DistributionSupplierCard";
import { twMerge } from "tailwind-merge";
import { DiscoveryV2DistributionInformationDialog } from "./DiscoveryV2DistributionInformationDialog";
import { DistributionEducationDialog } from "./DistributionEducationDialog";
import { DistributionEducationDialogPage } from "./DistributionEducationDialog.shared";
import { IconType } from "@sellar-io/components";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { useShallow } from "zustand/shallow";

export const DiscoveryV2DistributionSuppliersBanner = ({
  unAuthorisedUserPostcode,
  contentClassName,
  className,
  onTrackSupplierClick,
  onTrackBannerClick,
}: {
  unAuthorisedUserPostcode?: string;
  contentClassName?: string;
  className?: string;
  onTrackSupplierClick: (params: { supplierName: string }) => void;
  onTrackBannerClick: () => void;
}) => {
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const [
    openDistributionInformationDialog,
    setOpenDistributionInformationDialog,
  ] = useState(false);

  const buyerPostcode =
    business?.DeliveryAddress.postalCode || unAuthorisedUserPostcode || "";

  const hasBuyerPostcode = Boolean(buyerPostcode);

  const {
    setSearchValue,
    setIsDeliveredBySellar,
    setDeliveredBySellarOption,
    setSelectedSuppliers,
    reset,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setSearchValue: state.setSearchValue,
      setSelectedSuppliers: state.setSelectedSuppliers,
      reset: state.reset,
    })),
  );

  const discoveryDistributionSupplierListQuery =
    useDiscoveryDistributionSupplierList(
      {
        query: [
          {
            type: "retailerPostalCode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
        limit: 20,
      },
      {
        enabled: hasBuyerPostcode,
      },
    );

  const handleNavigate = () => {
    onTrackBannerClick();
    navigate(DISCOVER_DISTRIBUTION);
  };

  const suppliers =
    discoveryDistributionSupplierListQuery.data?.pages.flat() ?? [];

  if (!hasBuyerPostcode || suppliers.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={twMerge(
          "flex w-full flex-col items-center bg-[#E8D3F433]",
          className,
        )}
      >
        <div
          className={twMerge(
            "flex w-full max-w-300 flex-col gap-0 rounded-md",
            contentClassName,
          )}
        >
          <DiscoveryV2DistributionHeader
            buyerPostcode={buyerPostcode}
            onNavigateToDiscoveryDistributionLandingPage={handleNavigate}
            onNavigateToMarket={() => {
              setSearchValue("");
              setIsDeliveredBySellar(true);
              setDeliveredBySellarOption("all");
              navigate(DISCOVER_SEARCH);
            }}
          />

          <DiscoveryHorizontalList
            hideViewMore={false}
            containerClassName="px-0"
            onClick={handleNavigate}
          >
            <div className="flex gap-3">
              {suppliers.map((supplier) => (
                <DiscoveryV2DistributionSupplierCard
                  key={supplier.id}
                  onClick={() => {
                    const supplierName = supplier.Business.name;
                    onTrackSupplierClick({
                      supplierName,
                    });

                    reset();
                    setSearchValue("");
                    setIsDeliveredBySellar(true);
                    setDeliveredBySellarOption("all");
                    setSelectedSuppliers([
                      {
                        id: supplier.id,
                        name: supplierName,
                      },
                    ]);

                    navigate(DISCOVER_SEARCH);
                  }}
                  imageUrl={supplier.imageUrl ?? ""}
                  logoUrl={supplier.Business.logoUrl ?? ""}
                  deliveryAddressCity={supplier.Business.DeliveryAddress.city}
                  businessName={supplier.Business.name}
                />
              ))}
            </div>
          </DiscoveryHorizontalList>
        </div>
      </div>
      <DiscoveryV2DistributionInformationDialog
        open={openDistributionInformationDialog}
        onDismiss={() => {
          setOpenDistributionInformationDialog(false);
        }}
        onConfirm={() => {
          setOpenDistributionInformationDialog(false);
        }}
      />
    </>
  );
};

type DiscoveryV2DistributionHeaderProps = {
  buyerPostcode: string;
  onNavigateToDiscoveryDistributionLandingPage: () => void;
  onNavigateToMarket: () => void;
};
const DiscoveryV2DistributionHeader = ({
  buyerPostcode,
  onNavigateToDiscoveryDistributionLandingPage,
  onNavigateToMarket,
}: DiscoveryV2DistributionHeaderProps) => {
  const isMobile = useMediaQuery("(max-width: 767px)");

  const [openDistributionEducationDialog, setOpenDistributionEducationDialog] =
    useState(false);

  const renderDeliveredBySellarText = () => {
    return (
      <div className="inline-flex gap-1 text-lg">
        Delivered by
        <span className="inline-flex items-center gap-1 text-[#8C23C7]">
          Sellar <Icon icon="SELLAR_DISTRIBUTION_TRUCK" />
        </span>
      </div>
    );
  };

  const flexibleDelivery = (
    <span>
      <strong className="font-semibold text-[#8C23C7]">
        Flexible, expert delivery
      </strong>{" "}
      - no couriers or pallets
    </span>
  );

  const freeWithNoMinimums = (
    <span>
      <strong className="font-semibold text-[#8C23C7]">Free</strong> with{" "}
      <strong className="font-semibold text-[#8C23C7]">no minimums</strong>{" "}
    </span>
  );

  const multipleSuppliersDeliveredTogether = (
    <span>
      Multiple suppliers{" "}
      <strong className="font-semibold text-[#8C23C7]">
        delivered together
      </strong>
    </span>
  );

  if (isMobile) {
    return (
      <>
        <div
          role="button"
          onClick={(event) => {
            event.stopPropagation();
            onNavigateToDiscoveryDistributionLandingPage();
          }}
          className="flex cursor-pointer flex-col gap-3 rounded-[15px] border border-zinc-300 bg-zinc-50 p-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-zinc-800">
              {renderDeliveredBySellarText()}
            </div>
            <Button color="slim" className="shadow-sm">
              <Icon icon="INFORMATION_OUTLINE" />
            </Button>
          </div>

          <div className="flex flex-col gap-2">
            <CustomBannerButton
              title="Get it by the weekend"
              subtitle="See what you can get delivered together by the weekend"
              icon="CLOCK_OUTLINE"
              onClick={(event) => {
                event.stopPropagation();
                onNavigateToDiscoveryDistributionLandingPage();
              }}
            />
            <CustomBannerButton
              title="Browse all products"
              subtitle="Shop from the whole range of suppliers delivered by Sellar"
              icon="CALENDAR_CHECK_OUTLINE"
              onClick={(event) => {
                event.stopPropagation();
                onNavigateToMarket();
              }}
            />
          </div>
        </div>

        <DistributionEducationDialog
          active={openDistributionEducationDialog}
          buyerPostcode={buyerPostcode}
          supplierId={null}
          initialPage={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}
          onDismiss={() => setOpenDistributionEducationDialog(false)}
        />
      </>
    );
  }

  return (
    <>
      <div
        role="button"
        onClick={onNavigateToDiscoveryDistributionLandingPage}
        className="flex cursor-pointer flex-col gap-4 rounded-[15px] border border-zinc-300 bg-zinc-50 p-4"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex w-full flex-1 items-center gap-4">
            <div className="flex h-14 min-h-14 w-14 min-w-14 flex-col items-center justify-center rounded-full bg-[#E8D3F4]/20">
              <div className="flex h-10 min-h-10 w-10 min-w-10 items-center justify-center rounded-full bg-[#E8D3F4]">
                <Icon
                  icon="SELLAR_DISTRIBUTION_TRUCK"
                  viewBox="0 0 16 16"
                  className="size-6 text-[#8C23C7]"
                />
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-0">
              <div className="flex items-center gap-2 font-semibold text-zinc-800">
                <span className="text-lg">Delivered by Sellar</span>
              </div>
              <ul className="flex flex-1 flex-row flex-wrap gap-2 text-sm text-zinc-500">
                <li className="flex items-center gap-1">
                  <span className="flex shrink-0 items-center justify-center leading-none">
                    <Icon
                      icon="CHECKBOX_MARKED_CIRCLE"
                      className="text-[#BA7BDD]"
                      size={0.75}
                    />
                  </span>
                  {flexibleDelivery}
                </li>
                <li className="flex items-center gap-1">
                  <span className="flex shrink-0 items-center justify-center leading-none">
                    <Icon
                      icon="CHECKBOX_MARKED_CIRCLE"
                      className="text-[#BA7BDD]"
                      size={0.75}
                    />
                  </span>
                  {freeWithNoMinimums}
                </li>
                <li className="flex items-center gap-1">
                  <span className="flex shrink-0 items-center justify-center leading-none">
                    <Icon
                      icon="CHECKBOX_MARKED_CIRCLE"
                      className="text-[#BA7BDD]"
                      size={0.75}
                    />
                  </span>
                  {multipleSuppliersDeliveredTogether}
                </li>
              </ul>
            </div>
          </div>
          <div className="flex cursor-pointer items-center gap-2">
            <Button
              color="slim"
              className="shadow-sm"
              onClick={(event) => {
                event.stopPropagation();
                setOpenDistributionEducationDialog(true);
              }}
            >
              How does it work?
            </Button>
          </div>
        </div>
        <div className="flex gap-4">
          <CustomBannerButton
            title="Get it by the weekend"
            subtitle="See what you can get delivered together by the weekend"
            icon="CLOCK_OUTLINE"
            onClick={(event) => {
              event.stopPropagation();
              onNavigateToDiscoveryDistributionLandingPage();
            }}
          />
          <CustomBannerButton
            title="Browse all products"
            subtitle="Shop from the whole range of suppliers delivered by Sellar"
            icon="CALENDAR_CHECK_OUTLINE"
            onClick={(event) => {
              event.stopPropagation();
              onNavigateToMarket();
            }}
          />
        </div>
      </div>
      <DistributionEducationDialog
        active={openDistributionEducationDialog}
        buyerPostcode={buyerPostcode}
        supplierId={null}
        initialPage={DistributionEducationDialogPage.EXPERT_DRAY_DELIVERY}
        onDismiss={() => setOpenDistributionEducationDialog(false)}
      />
    </>
  );
};

type CustomBannerButtonProps = {
  title: string;
  subtitle: string;
  icon: IconType;
  onClick?: ComponentProps<"div">["onClick"];
};
const CustomBannerButton = ({
  title,
  subtitle,
  icon,
  onClick,
}: CustomBannerButtonProps) => {
  return (
    <div
      role="button"
      onClick={onClick}
      className="flex flex-1 cursor-pointer items-center gap-4 rounded-2xl border border-zinc-300 bg-white p-4 shadow-md"
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#A34FD2]">
        <Icon icon={icon} className="text-white" size={0.8} />
      </div>
      <div className="flex flex-1 flex-col">
        <p className="font-semibold text-zinc-800">{title}</p>
        <p className="text-sm text-zinc-600">{subtitle}</p>
      </div>
      <Icon icon="CHEVRON_RIGHT" size={1} />
    </div>
  );
};
